<template>
    <b-container style="width:600px;">
        <b-card>
            <h3>Activate 1.3 Course</h3>
            <br />
            <b-card-text>
                <b-input-group prepend="Token">
                    <b-form-input v-model="form.token" @keyup.enter="getMembershipInfo" type="text"></b-form-input>
                    <b-input-group-append>
                        <b-button variant="success" @click="getMembershipInfo">Submit
                            <b-spinner v-if="this.isSending" class="m-1" label="Spinning" small />
                        </b-button>
                    </b-input-group-append>
                </b-input-group>
                <ul class="mt-3">
                    <li v-for="error in errors" :key="error" style="color:red">{{ error }}</li>
                 </ul>

            </b-card-text>

            <div v-if="invalid">
                Token field needs to have a value.
            </div>

            <div v-if="this.membershipInfo">
                <p>
                    CID: {{ this.membershipInfo.context.context_id }}<br>
                    Title: {{ this.membershipInfo.context.context_title }}<br>
                    Issuer: {{ this.membershipInfo.context.deployment.credential.platform.iss }}<br>
                </p>

                <p v-if="this.data && this.data.coupon_code">
                    Coupon Code: {{ this.data.coupon_code }}
                </p>

                Product:
                <span class="text-success font-weight-bold">{{ this.product_code }}</span>
                <b-alert
                  v-if="!this.product_code && this.membershipInfo && this.membershipInfo.context.deployment.credential.product_id"
                  show variant="info" class="mt-2 mb-2 py-2">
                  <small>
                    <b-icon icon="info-circle"></b-icon>
                    On next launch, this course will be auto-activated with
                    <strong>{{ productLabel(this.membershipInfo.context.deployment.credential.product_id) }}</strong>
                    from the credential's auto-activate setting.
                  </small>
                </b-alert>
                <b-form-select class="mt-2 mb-2" v-model="form.product_code" :options="products_options" :select-size="12">
                </b-form-select>
                <p>
                    <b-button class="mt-2" size="sm" text="Button" variant="primary" @click="activateCourse" :disabled="this.form.product_code == this.product_code && this.form.shows_teacher_resources === this.original_shows_teacher_resources && this.form.allows_wts === this.original_allows_wts && this.form.auto_enroll_coupon_code === this.original_auto_enroll_coupon_code">
                        Change
                    </b-button>
                </p>
                <!-- Teacher Resources Toggle -->
                <div class="d-flex align-items-center my-2">
                    <span class="mr-2">Teacher Resources:</span>
                    <b-form-select v-model="form.shows_teacher_resources" size="sm" style="width: 200px;">
                        <option :value="null">Inherit from Credential</option>
                        <option :value="true">Enabled</option>
                        <option :value="false">Disabled</option>
                    </b-form-select>
                </div>

                <!-- ASL Dictionary (WTS) Toggle -->
                <div class="d-flex align-items-center my-2">
                    <span class="mr-2">ASL Dictionary (WTS):</span>
                    <b-form-select v-model="form.allows_wts" size="sm" style="width: 200px;">
                        <option :value="null">Inherit from Credential</option>
                        <option :value="true">Enabled</option>
                        <option :value="false">Disabled</option>
                    </b-form-select>
                </div>

                <!-- Auto-Enroll Access Code (TWA-231: course-level binding) -->
                <div class="d-flex align-items-center my-1">
                    <span class="mr-2">Auto-Enroll Access Code:</span>
                    <b-form-input v-model.trim="form.auto_enroll_coupon_code" size="sm"
                      style="width: 260px;" placeholder="Blank = inherit from school"></b-form-input>
                </div>
                <small v-if="membershipInfo && membershipInfo.context.auto_enroll_coupon_code" class="text-muted d-block">
                    Auto-Enroll (Course): <strong>{{ membershipInfo.context.auto_enroll_coupon_code }}</strong>
                    ({{ membershipInfo.context.auto_enroll_product_code }}) —
                    {{ membershipInfo.context.auto_enroll_usage }}/{{ membershipInfo.context.auto_enroll_max_usage }} used
                </small>
                <b-alert v-if="autoEnrollMismatch" show variant="warning" class="mt-1 py-1 px-2">
                    <small>
                        This code's product ({{ membershipInfo.context.auto_enroll_product_code }}) does not
                        match this course's product ({{ product_code }}). Students will be
                        <strong>blocked</strong> — auto-enroll offers no pay/access-code fallback.
                        Fix the code or clear the field to inherit from the school.
                    </small>
                </b-alert>
                <small v-if="membershipInfo" class="text-muted d-block">
                    Auto-Enroll (School):
                    <span v-if="membershipInfo.context.deployment.credential.auto_enroll_coupon_code">
                        {{ membershipInfo.context.deployment.credential.auto_enroll_coupon_code }}
                        ({{ membershipInfo.context.deployment.credential.auto_enroll_product_code }}) —
                        {{ membershipInfo.context.deployment.credential.auto_enroll_usage }}/{{ membershipInfo.context.deployment.credential.auto_enroll_max_usage }} used
                    </span>
                    <span v-else>None</span>
                    · <router-link :to="`/manage_platform?search=${encodeURIComponent(schoolLabel)}`">{{ schoolLabel }}</router-link>
                </small>

                <!-- TWA-275: WTS + auto-enroll both effectively on. Not an error —
                     manually rostered premium KSS schools run this deliberately
                     (TWA-283) — so this states the consequence and lets the admin judge. -->
                <!-- A plain div with v-if, not <b-alert>: b-alert kept a stale warning on
                     screen when the condition went false (see Course.vue). -->
                <div v-if="wtsAutoEnrollBoth" class="alert alert-warning mt-2 py-2 px-2" role="alert">
                    <small>
                        <strong>ASL Dictionary is on and an auto-enroll code is set
                        ({{ wtsAutoEnrollBoth.wtsFrom }} / {{ wtsAutoEnrollBoth.codeFrom }}).</strong>
                        Students reach the dictionary without signing up, so they will not be
                        enrolled, no seats will be claimed, and no grade is passed back.
                        Fine if this school is manually rostered; not fine if students are
                        meant to complete the Sign Up module.
                    </small>
                </div>

                <p class="mt-3">
                <b-form-checkbox v-model="form.create_coupon" name="create_coupon" value=true unchecked-value=false>
                    Create Test Coupon?
                </b-form-checkbox>
                </p>
                <p>
                    Memberships:
                    <ul class="mt-3">
                        <li v-for="membership in this.membershipInfo.context.memberships" :key="membership">
                            {{ membership.email }}, {{ membership.role }} <span v-if="membership.token == form.token">*</span>
                        </li>
                    </ul>
                </p>
            </div>
            <div v-if="activations">
                Activation History:
                <b-table :bordered="true" :small="true" :items="activation_history"></b-table>
            </div>
        </b-card>
    </b-container>
</template>

<script>
import {activateCourse, getProducts, getMembership, getUsers} from "../../utils/twa-api";
import { getErrorMessage, getDetailedErrorMessage } from "@/utils/error-messages";
import notify from "@/mixins/notify";

export default {
    mixins: [notify],
    data() {
        return {
            form: {
                token: this.$route.params.token,
                product_code: '',
                shows_teacher_resources: null,
                allows_wts: null,
                auto_enroll_coupon_code: '',
                create_coupon: false
            },
            isSending: false,
            isProcessing: false,
            data: null,
            invalid: false,
            product_code: '',
            token: '',
            membershipInfo: null,
            errors: [],
            fields: ['email', 'role', 'associated'],
            selected_products: [],
            activations: null,
            activation_history: [],
            original_shows_teacher_resources: null,
            original_allows_wts: null,
            original_auto_enroll_coupon_code: '',
            allProducts: [],
            allUsers: [],
        }
    },
    computed: {
        products () {
            return this.allProducts.filter(p => p.name.toLowerCase() !== 'zfree').sort((a, b) => a.name.localeCompare(b.name))
        },
        products_options() {
            return this.products.map(function (u) {
                return { value: u.product_code, text: u.name + ' (' + u.product_code + ')' }
            })
        },
        users () {
            return [...this.allUsers].sort((a, b) => a.id - b.id)
        },
        // TWA-275: are WTS and auto-enroll BOTH effectively on for this context?
        // Returns {wtsFrom, codeFrom} naming where each comes from, or null.
        //
        // Reads the pending form values. Both settings are tri-state (NULL = inherit
        // from the credential), and the case that matters most is a context inheriting
        // both, where neither control reads "on".
        wtsAutoEnrollBoth() {
            const ctx = this.membershipInfo && this.membershipInfo.context
            if (!ctx) return null
            // An un-activated context can't have the problem: launch_controller gates
            // on context.product before ever reaching the WTS branch. Pending selection,
            // not the saved product, so the note fires on the activating save itself.
            if (!this.form.product_code) return null

            const cred = ctx.deployment.credential
            const formWts = this.form.allows_wts
            const inheritsWts = formWts === null || formWts === undefined
            const wtsOn = inheritsWts ? !!cred.allows_wts : !!formWts
            if (!wtsOn) return null

            const formCode = (this.form.auto_enroll_coupon_code || '').trim()
            if (!formCode && !cred.auto_enroll_coupon_code) return null

            // Only while the admin is creating this state (TWA-275 hand-off point 2): an
            // existing both-set context is usually deliberate after the premium-KSS
            // sweep (TWA-283), so it gets no badge on load. The product counts as a
            // change so the inherit-both case still fires — there the admin touches
            // neither switch, only the product.
            const changed =
                this.form.product_code !== this.product_code ||
                formWts !== this.original_allows_wts ||
                formCode !== (this.original_auto_enroll_coupon_code || '')
            if (!changed) return null

            return {
                wtsFrom: inheritsWts ? 'inherited from the school' : 'set on this course',
                codeFrom: formCode ? 'code on this course' : 'code from the school',
            }
        },
        autoEnrollMismatch() {
            const ctx = this.membershipInfo && this.membershipInfo.context
            if (!ctx || !ctx.auto_enroll_coupon_code) return false
            return ctx.auto_enroll_product_code !== this.product_code
        },
        schoolLabel() {
            const cred = this.membershipInfo && this.membershipInfo.context.deployment.credential
            if (!cred) return ''
            // school_name can be blank on shared/dev credentials — fall back to client_id
            return cred.school_name || cred.client_id
        },
    },
    methods: {
        handleLoginEvent(data) {
            this.isAuthenticated = data.loggedIn
            this.profile = data.profile
        },
        productLabel(productId) {
            const p = this.allProducts.find(x => x.id === productId)
            return p ? `${p.name} (${p.product_code})` : `product #${productId}`
        },
        async getData() {
            try {
                this.allProducts = await getProducts()
                this.allUsers = await getUsers()
            } catch (error) {
                this.notifyMe(getErrorMessage(error))
                if (error.response && error.response.status === 401) {
                    this.$auth.logOut('Unauthorized.')
                }
            }
        },
        async getMembershipInfo(){
            this.isSending = true
            this.membershipInfo = null
            this.errors = []
            this.form.product_code = ''
            this.product_code = ''
            // Reset the per-course form fields too, so a stale value from a prior
            // lookup can't carry over and get silently bound to a DIFFERENT course
            // (an unactivated course skips the repopulation block below).
            this.form.auto_enroll_coupon_code = ''
            this.original_auto_enroll_coupon_code = ''
            this.form.shows_teacher_resources = null
            this.original_shows_teacher_resources = null
            this.form.allows_wts = null
            this.original_allows_wts = null
            this.activations = null

            // Defensive: ensure products are loaded before rendering the
            // transparency banner (mounted-time fetch may not have resolved
            // on a fast first click), otherwise productLabel falls back to
            // "product #5" instead of the resolved name + code.
            if (this.allProducts.length === 0) {
                await this.getData()
            }

            try {
                this.membershipInfo = await getMembership(this.form.token);
                if (this.membershipInfo.context.product)  {
                    this.product_code = this.membershipInfo.context.product.product_code
                    this.form.product_code = this.product_code
                    this.form.shows_teacher_resources = this.membershipInfo.context.shows_teacher_resources != null ? this.membershipInfo.context.shows_teacher_resources : null
                    this.original_shows_teacher_resources = this.form.shows_teacher_resources
                    this.form.allows_wts = this.membershipInfo.context.allows_wts != null ? this.membershipInfo.context.allows_wts : null
                    this.original_allows_wts = this.form.allows_wts
                    this.form.auto_enroll_coupon_code = this.membershipInfo.context.auto_enroll_coupon_code || ''
                    this.original_auto_enroll_coupon_code = this.form.auto_enroll_coupon_code
                    this.activations = this.membershipInfo.context.activations
                    this.generateActivationHistory()
                }
            } catch (error) {
                if (error.response && error.response.status === 404) {
                    this.errors.push("Membership not found");
                } else if (error.response && error.response.status === 500) {
                    this.errors.push("Internal Server Error");
                } else {
                    console.log(error)
                }
            }
            this.isSending = false
        },
        async activateCourse() {
            this.isSending = true
            this.errors = []

            try {
                if (this.form.token) {
                    this.data = await activateCourse(this.form.token, this.form.product_code, this.form.create_coupon, this.form.shows_teacher_resources, this.form.auto_enroll_coupon_code, this.form.allows_wts)
                    this.invalid = false
                    // Re-fetch the full membership (get_membership carries the roster + the
                    // injected auto_enroll summaries) so readouts + the mismatch warning are fresh.
                    await this.getMembershipInfo()
                }
                else {
                    this.invalid = true
                }
            } catch (error) {
                // Surface the bind rejection (e.g. 422 unknown code) instead of swallowing
                // it — otherwise the admin believes auto-enroll is armed when the code was
                // refused.
                this.errors.push(getDetailedErrorMessage(error))
            }

            this.isSending = false
        },
        generateActivationHistory(){
            this.activation_history = []

            this.activations.forEach(element => {
                let product = this.products.find(product => element.product_id == product.id)
                this.activation_history.push({
                    date: new Date(element.activated_at).toLocaleString(),
                    product_code: product ? product.product_code : 'unknown',
                    user: element.actor_label
                })
            })
        },
    },
    mounted () {
        this.getData()
  }
}
</script>
